import React, { useState, useEffect, useRef } from 'react';
import { 
  ExclamationTriangleIcon, 
  TagIcon, 
  XMarkIcon, 
  CheckIcon, 
  TableCellsIcon, 
  DocumentIcon,
  CloudArrowUpIcon,
  CogIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  InformationCircleIcon,
  ServerIcon,
  ClipboardDocumentCheckIcon,
  CurrencyEuroIcon,
  CalendarIcon,
  UserGroupIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import ConfirmationModal from './ConfirmationModal';
import apiClient from '../utils/apiClient';

interface Account {
  id: string;
  name: string;
  kontotyp: string;
}

interface Transaction {
  buchungstag: string;
  zahlungsbeteiligter: string;
  buchungstext: string;
  verwendungszweck: string;
  betrag: number;
  saldo: number;
  konto_id?: string;
}

interface CSVImporterProps {
  onImportSuccess?: () => void;
  onClose?: () => void;
}

const CSVImporter: React.FC<CSVImporterProps> = ({ onImportSuccess, onClose }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [delimiter, setDelimiter] = useState(';');
  const [thousandsSeparator, setThousandsSeparator] = useState('.');
  const [decimalSeparator, setDecimalSeparator] = useState(',');
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [columnTypes, setColumnTypes] = useState<string[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [columnMapping, setColumnMapping] = useState({
    buchungstag: '',
    zahlungsbeteiligter: '',
    buchungstext: '',
    verwendungszweck: '',
    betrag: '',
    saldo: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [importableTransactions, setImportableTransactions] = useState<Transaction[]>([]);
  const [existingTransactions, setExistingTransactions] = useState<Transaction[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importMessage, setImportMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'info' as 'success' | 'warning' | 'error' | 'question' | 'info',
    title: '',
    message: '',
    variant: 'single' as 'single' | 'two',
    onConfirm: () => {},
  });

  // Fetch accounts on mount
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await apiClient.get('/api/finance/accounts');
        const data = response.data;
        setAccounts(data);
        if (data.length > 0) setSelectedAccount(data[0].id);
      } catch (err: any) {
        setError('Fehler beim Laden der Konten: ' + err.message);
      }
    };
    fetchAccounts();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const detectType = (value: string) => {
    if (!value) return 'string';
    let cleanedValue = value.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
    if (decimalSeparator !== '.') cleanedValue = cleanedValue.replace(decimalSeparator, '.');
    if (/^-?\d+$/.test(cleanedValue)) return 'integer';
    if (/^-?\d*\.?\d+$/.test(cleanedValue)) return 'float';
    if (/^\d{4}-\d{2}-\d{2}$/.test(value) || /^\d{2}\.\d{2}\.\d{4}$/.test(value)) return 'date';
    return 'string';
  };

  const convertGermanDate = (dateStr: string) => {
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      const [day, month, year] = dateStr.split('.');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const isValidDate = (dateStr: string) => {
    let normalizedDate = dateStr;
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(dateStr)) {
      normalizedDate = convertGermanDate(dateStr);
    }
    const date = new Date(normalizedDate);
    return date instanceof Date && !isNaN(date.getTime()) && normalizedDate === date.toISOString().split('T')[0];
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      loadFile(selectedFile);
      setCurrentStep(2);
    } else {
      setError('Bitte wählen Sie eine CSV-Datei aus');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'text/csv') {
      setFile(droppedFile);
      loadFile(droppedFile);
      setCurrentStep(2);
    } else {
      setError('Bitte nur CSV-Dateien hochladen');
    }
  };

  const loadFile = (fileToLoad: File) => {
    setError(null);
    setSuccess(null);
    setImportableTransactions([]);
    setExistingTransactions([]);
    setShowConfirmModal(false);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        processCSV(text);
        setSuccess(`Datei "${fileToLoad.name}" erfolgreich geladen`);
      } catch (err: any) {
        setError('Fehler beim Verarbeiten der CSV-Datei: ' + err.message);
      }
    };
    reader.readAsText(fileToLoad);
  };

  const processCSV = (text: string) => {
    const lines = text.split('\n').filter(line => line.trim());
    if (lines.length < 2) throw new Error('CSV-Datei enthält nicht genügend Daten');

    const headersFound = lines[0].split(delimiter).map(h => h.trim());
    setHeaders(headersFound);

    const preview = lines.slice(0, 6).map(line => line.split(delimiter).map(item => item.trim()));
    const types = headersFound.map((_, colIndex) => {
      const columnValues = lines.slice(1, 6)
        .map(line => line.split(delimiter)[colIndex]?.trim())
        .filter(Boolean);
      if (columnValues.every(val => detectType(val) === 'integer')) return 'integer';
      if (columnValues.every(val => detectType(val) === 'float')) return 'float';
      if (columnValues.every(val => detectType(val) === 'date')) return 'date';
      return 'string';
    });

    const newMapping: any = { buchungstag: '', zahlungsbeteiligter: '', buchungstext: '', verwendungszweck: '', betrag: '', saldo: '' };
    headersFound.forEach(header => {
      const lowercaseHeader = header.toLowerCase().trim();
      if (['buchungstag', 'datum', 'date'].includes(lowercaseHeader)) newMapping.buchungstag = header;
      if (['name zahlungsbeteiligter', 'empfänger', 'payee'].includes(lowercaseHeader)) newMapping.zahlungsbeteiligter = header;
      if (['buchungstext', 'beschreibung', 'description'].includes(lowercaseHeader)) newMapping.buchungstext = header;
      if (['verwendungszweck', 'zweck', 'purpose'].includes(lowercaseHeader)) newMapping.verwendungszweck = header;
      if (['betrag', 'amount'].includes(lowercaseHeader)) newMapping.betrag = header;
      if (['saldo nach buchung', 'saldo', 'balance'].includes(lowercaseHeader)) newMapping.saldo = header;
    });

    setColumnMapping(newMapping);
    setColumnTypes(types);
    setPreviewData(preview);
  };

  const handleDelimiterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDelimiter(e.target.value);
    if (file) loadFile(file);
  };

  const handleThousandsSeparatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setThousandsSeparator(e.target.value);
    if (file) loadFile(file);
  };

  const handleDecimalSeparatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setDecimalSeparator(e.target.value);
    if (file) loadFile(file);
  };

  const handleMappingChange = (field: string, value: string) => {
    setColumnMapping(prev => ({ ...prev, [field]: value }));
  };

  const handleCheckImport = async () => {
    if (!file || !selectedAccount) {
      setError('Bitte wählen Sie eine Datei und ein Konto aus');
      return;
    }

    setIsChecking(true);
    setError(null);
    setSuccess(null);

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) throw new Error('CSV-Datei enthält nicht genügend Daten');

        const result = lines.slice(1).map((line, index) => {
          const values = line.split(delimiter);
          let buchungstag = values[headers.indexOf(columnMapping.buchungstag)]?.trim() || '';
          if (/^\d{2}\.\d{2}\.\d{4}$/.test(buchungstag)) buchungstag = convertGermanDate(buchungstag);
          if (!isValidDate(buchungstag)) throw new Error(`Ungültiges Datum in Zeile ${index + 2}`);

          return {
            buchungstag,
            zahlungsbeteiligter: values[headers.indexOf(columnMapping.zahlungsbeteiligter)]?.trim() || '',
            buchungstext: values[headers.indexOf(columnMapping.buchungstext)]?.trim() || '',
            verwendungszweck: values[headers.indexOf(columnMapping.verwendungszweck)]?.trim() || '',
            betrag: parseFloat(values[headers.indexOf(columnMapping.betrag)]?.trim().replace(thousandsSeparator, '').replace(decimalSeparator, '.') || '0') || 0,
            saldo: parseFloat(values[headers.indexOf(columnMapping.saldo)]?.trim().replace(thousandsSeparator, '').replace(decimalSeparator, '.') || '0') || 0,
            konto_id: selectedAccount,
          };
        });

        const response = await apiClient.post('/api/finance/statements/check', result);
        const { importable, existing } = response.data;
        setImportableTransactions(importable || []);
        setExistingTransactions(existing || []);
        setShowConfirmModal(true);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsChecking(false);
      }
    };
    reader.readAsText(file);
  };

  const handleConfirmImport = async () => {
    try {
      setShowConfirmModal(false);
      setIsImporting(true);
      setImportProgress(0);
      setImportMessage('Verbindung zum Server wird hergestellt...');
      
      // Simulate progress steps
      const progressSteps = [
        { progress: 20, message: 'Daten werden validiert...' },
        { progress: 40, message: 'Transaktionen werden verarbeitet...' },
        { progress: 60, message: 'Duplikate werden überprüft...' },
        { progress: 80, message: 'Daten werden in die Datenbank geschrieben...' },
      ];
      
      let currentStepIndex = 0;
      const progressInterval = setInterval(() => {
        if (currentStepIndex < progressSteps.length) {
          setImportProgress(progressSteps[currentStepIndex].progress);
          setImportMessage(progressSteps[currentStepIndex].message);
          currentStepIndex++;
        }
      }, 500);

      const response = await apiClient.post('/api/finance/statements/import', importableTransactions);

      clearInterval(progressInterval);
      
      const { importedCount } = response.data;
      
      // Complete animation
      setImportProgress(100);
      setImportMessage(`✅ ${importedCount} Transaktionen erfolgreich importiert!`);
      
      setTimeout(() => {
        setIsImporting(false);
        setConfirmModal({
          isOpen: true,
          type: 'success',
          title: 'Import erfolgreich',
          message: `${importedCount} Transaktionen wurden importiert, ${existingTransactions.length} Duplikate übersprungen.`,
          variant: 'single',
          onConfirm: () => {
            setIsOpen(false);
            if (onImportSuccess) {
              onImportSuccess();
            }
          }
        });
      }, 1000);
    } catch (err: any) {
      setIsImporting(false);
      setError(err.message);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'integer': return 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400 border border-success-200 dark:border-success-800';
      case 'float': return 'bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400 border border-info-200 dark:border-info-800';
      case 'date': return 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-400 border border-primary-200 dark:border-primary-800';
      default: return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'integer': return '123';
      case 'float': return '1.23';
      case 'date': return '📅';
      default: return 'ABC';
    }
  };

  const getExampleValue = (header: string) => previewData[1]?.[headers.indexOf(header)] || '';

  const isAutoSelected = (header: string, field: string) => {
    const lowercaseHeader = header.toLowerCase().trim();
    switch (field) {
      case 'buchungstag': return ['buchungstag', 'datum', 'date'].includes(lowercaseHeader);
      case 'zahlungsbeteiligter': return ['name zahlungsbeteiligter', 'empfänger', 'payee'].includes(lowercaseHeader);
      case 'buchungstext': return ['buchungstext', 'beschreibung', 'description'].includes(lowercaseHeader);
      case 'verwendungszweck': return ['verwendungszweck', 'zweck', 'purpose'].includes(lowercaseHeader);
      case 'betrag': return ['betrag', 'amount'].includes(lowercaseHeader);
      case 'saldo': return ['saldo nach buchung', 'saldo', 'balance'].includes(lowercaseHeader);
      default: return false;
    }
  };

  // Import Animation Overlay
  const ImportAnimationOverlay = () => {
    if (!isImporting) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[70] p-4">
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl md:rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-100 dark:border-gray-800/50">
          <div className="flex flex-col items-center space-y-6">
            <div className="relative w-32 h-32">
              <div className="absolute inset-0 border-4 border-gray-200 dark:border-gray-700 rounded-full"></div>
              <div 
                className="absolute inset-0 border-4 border-transparent border-t-primary-500 rounded-full animate-spin"
                style={{ animationDuration: '1.5s' }}
              ></div>
              
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  {importProgress < 30 && (
                    <DocumentIcon className="h-12 w-12 text-primary-500 animate-pulse" />
                  )}
                  {importProgress >= 30 && importProgress < 60 && (
                    <ServerIcon className="h-12 w-12 text-primary-500 animate-pulse" />
                  )}
                  {importProgress >= 60 && importProgress < 90 && (
                    <TableCellsIcon className="h-12 w-12 text-info-500 animate-pulse" />
                  )}
                  {importProgress >= 90 && (
                    <CheckCircleIcon className="h-12 w-12 text-primary-500 animate-bounce" />
                  )}
                </div>
              </div>
              
              <svg className="absolute inset-0 w-32 h-32 transform -rotate-90">
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  className="text-gray-200 dark:text-gray-700"
                />
                <circle
                  cx="64"
                  cy="64"
                  r="60"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 60}`}
                  strokeDashoffset={`${2 * Math.PI * 60 * (1 - importProgress / 100)}`}
                  className="text-primary-500 transition-all duration-500 ease-out"
                  strokeLinecap="round"
                />
              </svg>
            </div>

            <div className="text-center space-y-2">
              <div className="text-3xl font-bold text-gray-900 dark:text-white">
                {importProgress}%
              </div>
              <div className="text-sm font-medium text-gray-600 dark:text-gray-400 animate-pulse">
                {importMessage}
              </div>
            </div>

            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full bg-primary-500 rounded-full transition-all duration-500 ease-out relative"
                style={{ width: `${importProgress}%` }}
              >
                <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
              </div>
            </div>

            {importProgress > 0 && (
              <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-primary-500 rounded-full animate-ping"></div>
                  <span>{importableTransactions.length} Transaktionen</span>
                </div>
                <div className="flex items-center gap-1">
                  <ClipboardDocumentCheckIcon className="h-3 w-3" />
                  <span>werden verarbeitet</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        <div className={`flex items-center ${currentStep >= 1 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
            currentStep >= 1 
              ? 'bg-primary-600 dark:bg-primary-500 border-primary-600 dark:border-primary-500 text-white' 
              : 'border-gray-300 dark:border-gray-600'
          }`}>
            {currentStep > 1 ? <CheckCircleIcon className="h-5 w-5" /> : '1'}
          </div>
          <span className="ml-2 font-medium text-sm">Datei hochladen</span>
        </div>
        
        <ArrowRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-600" />
        
        <div className={`flex items-center ${currentStep >= 2 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
            currentStep >= 2 
              ? 'bg-primary-600 dark:bg-primary-500 border-primary-600 dark:border-primary-500 text-white' 
              : 'border-gray-300 dark:border-gray-600'
          }`}>
            {currentStep > 2 ? <CheckCircleIcon className="h-5 w-5" /> : '2'}
          </div>
          <span className="ml-2 font-medium text-sm">Spalten zuordnen</span>
        </div>
        
        <ArrowRightIcon className="h-5 w-5 text-gray-400 dark:text-gray-600" />
        
        <div className={`flex items-center ${currentStep >= 3 ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-600'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${
            currentStep >= 3 
              ? 'bg-primary-600 dark:bg-primary-500 border-primary-600 dark:border-primary-500 text-white' 
              : 'border-gray-300 dark:border-gray-600'
          }`}>
            3
          </div>
          <span className="ml-2 font-medium text-sm">Importieren</span>
        </div>
      </div>
    </div>
  );

  // Confirm Modal Component - Preview before import
  const ImportPreviewModal = () => {
    if (!showConfirmModal) return null;

    const totalImportAmount = importableTransactions.reduce((sum, tx) => sum + tx.betrag, 0);
    const totalExistingAmount = existingTransactions.reduce((sum, tx) => sum + tx.betrag, 0);
    const importIncome = importableTransactions.filter(tx => tx.betrag > 0).reduce((sum, tx) => sum + tx.betrag, 0);
    const importExpenses = importableTransactions.filter(tx => tx.betrag < 0).reduce((sum, tx) => sum + Math.abs(tx.betrag), 0);

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
        <div className="bg-white dark:bg-[#1c2432] rounded-2xl md:rounded-3xl w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl border border-gray-100 dark:border-gray-800/50">
          <div className="flex justify-between items-center p-5 md:p-6 border-b border-gray-200 dark:border-gray-700/50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <TableCellsIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  Importvorschau
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Überprüfen Sie die Transaktionen vor dem Import
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowConfirmModal(false)}
              className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-5 md:p-6 bg-gray-50 dark:bg-[#0f1419]">
            <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 border border-gray-200 dark:border-gray-700/50">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Neue Transaktionen</p>
              <p className="text-2xl font-bold text-success-600 dark:text-success-400">{importableTransactions.length}</p>
            </div>
            <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 border border-gray-200 dark:border-gray-700/50">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Duplikate</p>
              <p className="text-2xl font-bold text-warning-600 dark:text-warning-400">{existingTransactions.length}</p>
            </div>
            <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 border border-gray-200 dark:border-gray-700/50">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Einnahmen</p>
              <p className="text-lg font-bold text-success-600 dark:text-success-400">{formatCurrency(importIncome)}</p>
            </div>
            <div className="bg-white dark:bg-[#1c2432] rounded-xl p-4 border border-gray-200 dark:border-gray-700/50">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1 uppercase tracking-wide">Ausgaben</p>
              <p className="text-lg font-bold text-error-600 dark:text-error-400">{formatCurrency(importExpenses)}</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {importableTransactions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded">
                      <CheckIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                    </div>
                    Neue Transaktionen
                  </h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Summe: {formatCurrency(totalImportAmount)}
                  </span>
                </div>
                <div className="overflow-x-auto bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Datum</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Empfänger/Sender</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300 hidden lg:table-cell">Verwendungszweck</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Betrag</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300 hidden md:table-cell">Saldo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {importableTransactions.slice(0, 50).map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3 text-gray-900 dark:text-gray-100 whitespace-nowrap">
                            {formatDate(row.buchungstag)}
                          </td>
                          <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
                            <div className="max-w-xs truncate" title={row.zahlungsbeteiligter}>
                              {row.zahlungsbeteiligter}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400 hidden lg:table-cell">
                            <div className="max-w-sm truncate" title={row.verwendungszweck}>
                              {row.verwendungszweck || '-'}
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${
                            row.betrag >= 0 ? 'text-success-600 dark:text-success-400' : 'text-error-600 dark:text-error-400'
                          }`}>
                            {formatCurrency(row.betrag)}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-600 dark:text-gray-400 hidden md:table-cell whitespace-nowrap">
                            {formatCurrency(row.saldo)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {importableTransactions.length > 50 && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                      ... und {importableTransactions.length - 50} weitere Transaktionen
                    </div>
                  )}
                </div>
              </div>
            )}

            {existingTransactions.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <div className="p-1.5 bg-warning-100 dark:bg-warning-900/30 rounded">
                      <ExclamationTriangleIcon className="h-4 w-4 text-warning-600 dark:text-warning-400" />
                    </div>
                    Bereits vorhandene Transaktionen (werden übersprungen)
                  </h4>
                  <span className="text-sm text-gray-500 dark:text-gray-400">
                    Summe: {formatCurrency(totalExistingAmount)}
                  </span>
                </div>
                <div className="overflow-x-auto bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Datum</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Empfänger/Sender</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300 hidden lg:table-cell">Verwendungszweck</th>
                        <th className="px-4 py-3 text-right font-medium text-gray-700 dark:text-gray-300">Betrag</th>
                        <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {existingTransactions.slice(0, 20).map((row, index) => (
                        <tr key={index} className="opacity-60 hover:opacity-100 transition-opacity">
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                            {formatDate(row.buchungstag)}
                          </td>
                          <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                            <div className="max-w-xs truncate" title={row.zahlungsbeteiligter}>
                              {row.zahlungsbeteiligter}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-gray-500 dark:text-gray-500 hidden lg:table-cell">
                            <div className="max-w-sm truncate" title={row.verwendungszweck}>
                              {row.verwendungszweck || '-'}
                            </div>
                          </td>
                          <td className={`px-4 py-3 text-right font-medium whitespace-nowrap ${
                            row.betrag >= 0 ? 'text-success-600/60 dark:text-success-400/60' : 'text-error-600/60 dark:text-error-400/60'
                          }`}>
                            {formatCurrency(row.betrag)}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400">
                              Duplikat
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {existingTransactions.length > 20 && (
                    <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
                      ... und {existingTransactions.length - 20} weitere Duplikate
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer: Abbrechen LINKS, Info MITTE, Aktion RECHTS */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-5 md:p-6 border-t border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-900/50">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium order-2 sm:order-1"
            >
              Abbrechen
            </button>
            <div className="text-sm text-gray-500 dark:text-gray-400 order-1 sm:order-2">
              {importableTransactions.length > 0 && (
                <span className="font-medium">
                  {importableTransactions.length} {importableTransactions.length === 1 ? 'Transaktion' : 'Transaktionen'}
                </span>
              )}
            </div>
            <button
              onClick={handleConfirmImport}
              disabled={importableTransactions.length === 0}
              className="px-5 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 font-medium order-3"
            >
              <CheckIcon className="h-4 w-4" />
              {importableTransactions.length > 0 ? 'Importieren' : 'Nichts zu importieren'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <ImportAnimationOverlay />
          <div className="bg-white dark:bg-[#1c2432] rounded-2xl md:rounded-3xl w-full max-w-5xl max-h-[95vh] flex flex-col shadow-2xl border border-gray-100 dark:border-gray-800/50">
            <div className="flex justify-between items-center p-5 md:p-6 border-b border-gray-200 dark:border-gray-700/50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                  <DocumentIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    CSV-Importer
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Importieren Sie Ihre Bankdaten schnell und einfach
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 md:p-6">
              <StepIndicator />

              {error && (
                <div className="mb-6 p-4 bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800/50 text-error-700 dark:text-error-300 rounded-xl flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">{error}</div>
                </div>
              )}

              {success && (
                <div className="mb-6 p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800/50 text-success-700 dark:text-success-300 rounded-xl flex items-start gap-3">
                  <CheckCircleIcon className="h-5 w-5 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">{success}</div>
                </div>
              )}

              {isChecking && (
                <div className="mb-6 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800/50 text-info-700 dark:text-info-300 rounded-xl flex items-center gap-3">
                  <div className="animate-spin h-5 w-5 border-2 border-current border-t-transparent rounded-full flex-shrink-0" />
                  <div className="flex-1">Prüfe Kontoauszüge...</div>
                </div>
              )}

              <div className="space-y-6">
                <section>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                        <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded">
                          <TagIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        Zielkonto wählen
                      </h3>
                      <select
                        value={selectedAccount}
                        onChange={(e) => setSelectedAccount(e.target.value)}
                        className="w-full px-4 py-3 border-0 rounded-xl bg-gray-50 dark:bg-[#0f1419] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm font-medium transition-all"
                      >
                        <option value="">-- Konto auswählen --</option>
                        {accounts.map((account) => (
                          <option key={account.id} value={account.id}>
                            {account.name} • {account.kontotyp}
                          </option>
                        ))}
                      </select>
                      {selectedAccount && (
                        <div className="mt-3 p-2 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                          <p className="text-xs text-primary-600 dark:text-primary-400 flex items-center gap-1">
                            <CheckCircleIcon className="h-3 w-3" />
                            Konto ausgewählt
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                        <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded">
                          <CloudArrowUpIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        CSV-Datei hochladen
                      </h3>
                      <div
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                          isDragging 
                            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' 
                            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500'
                        }`}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".csv"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                        {file ? (
                          <div className="space-y-2">
                            <DocumentIcon className="h-12 w-12 text-primary-500 mx-auto" />
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{file.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {(file.size / 1024).toFixed(2)} KB
                            </p>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="mt-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                            >
                              Andere Datei wählen
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <CloudArrowUpIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mx-auto" />
                            <div>
                              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Datei hier ablegen
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                oder
                              </p>
                            </div>
                            <button
                              onClick={() => fileInputRef.current?.click()}
                              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                            >
                              Datei auswählen
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {file && (
                    <div className="mt-6 bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                        <div className="p-1.5 bg-primary-100 dark:bg-primary-900/30 rounded">
                          <CogIcon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                        </div>
                        Formateinstellungen
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            Trennzeichen
                          </label>
                          <select
                            value={delimiter}
                            onChange={handleDelimiterChange}
                            className="w-full px-3 py-2.5 border-0 rounded-xl bg-gray-50 dark:bg-[#0f1419] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          >
                            <option value=";">Semikolon (;)</option>
                            <option value=",">Komma (,)</option>
                            <option value="\t">Tabulator</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            Tausendertrennzeichen
                          </label>
                          <select
                            value={thousandsSeparator}
                            onChange={handleThousandsSeparatorChange}
                            className="w-full px-3 py-2.5 border-0 rounded-xl bg-gray-50 dark:bg-[#0f1419] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          >
                            <option value=".">Punkt (.)</option>
                            <option value=",">Komma (,)</option>
                            <option value=" ">Leerzeichen</option>
                            <option value="">Keins</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                            Dezimaltrennzeichen
                          </label>
                          <select
                            value={decimalSeparator}
                            onChange={handleDecimalSeparatorChange}
                            className="w-full px-3 py-2.5 border-0 rounded-xl bg-gray-50 dark:bg-[#0f1419] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                          >
                            <option value=",">Komma (,)</option>
                            <option value=".">Punkt (.)</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  )}
                </section>

                {previewData.length > 0 && (
                  <>
                    <section className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                        <div className="p-1.5 bg-info-100 dark:bg-info-900/30 rounded">
                          <TableCellsIcon className="h-4 w-4 text-info-600 dark:text-info-400" />
                        </div>
                        Datenvorschau
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-normal ml-2">
                          (erste 5 Zeilen)
                        </span>
                      </h3>
                      <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              {previewData[0].map((header, index) => (
                                <th key={index} className="px-4 py-3 text-left bg-gray-50 dark:bg-gray-900">
                                  <div className="space-y-1">
                                    <div className="font-medium text-gray-700 dark:text-gray-300">
                                      {header}
                                    </div>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${getTypeColor(columnTypes[index])}`}>
                                      <span className="mr-1 font-mono text-[10px]">{getTypeIcon(columnTypes[index])}</span>
                                      {columnTypes[index]}
                                    </span>
                                  </div>
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                            {previewData.slice(1, 6).map((row, rowIndex) => (
                              <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                {row.map((cell, cellIndex) => (
                                  <td key={cellIndex} className="px-4 py-3 text-gray-900 dark:text-gray-100 font-mono text-xs">
                                    <div className="max-w-xs truncate" title={cell}>
                                      {cell || '-'}
                                    </div>
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </section>

                    <section className="bg-gray-50 dark:bg-gray-900 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center gap-2">
                          <div className="p-1.5 bg-amber-100 dark:bg-amber-900/30 rounded">
                            <TagIcon className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                          </div>
                          Spaltenzuordnung
                        </h3>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <InformationCircleIcon className="h-3 w-3" />
                          <span>Automatisch erkannte Spalten sind mit ✓ markiert</span>
                        </div>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {[
                          { field: 'buchungstag', label: 'Buchungsdatum', required: true, icon: <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" /> },
                          { field: 'zahlungsbeteiligter', label: 'Empfänger/Sender', required: true, icon: <UserGroupIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" /> },
                          { field: 'buchungstext', label: 'Buchungstext', required: true, icon: <DocumentTextIcon className="h-5 w-5 text-gray-600 dark:text-gray-400" /> },
                          { field: 'verwendungszweck', label: 'Verwendungszweck', required: true, icon: <PencilSquareIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" /> },
                          { field: 'betrag', label: 'Betrag', required: true, icon: <CurrencyEuroIcon className="h-5 w-5 text-primary-600 dark:text-primary-400" /> },
                          { field: 'saldo', label: 'Saldo', required: true, icon: <BanknotesIcon className="h-5 w-5 text-info-600 dark:text-info-400" /> }
                        ].map(({ field, label, required, icon }) => (
                          <div key={field} className="relative">
                            <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                              {icon}
                              {label}
                              {required && <span className="text-error-500">*</span>}
                            </label>
                            <div className="relative">
                              <select
                                value={(columnMapping as any)[field]}
                                onChange={(e) => {
                                  handleMappingChange(field, e.target.value);
                                  if (e.target.value && currentStep < 3) {
                                    const allMapped = Object.values({...columnMapping, [field]: e.target.value}).every(v => v);
                                    if (allMapped) setCurrentStep(3);
                                  }
                                }}
                                className={`w-full px-4 py-2.5 pr-10 rounded-xl bg-gray-50 dark:bg-[#0f1419] text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm transition-all ${
                                  (columnMapping as any)[field]
                                    ? 'ring-2 ring-success-500'
                                    : 'border-0'
                                }`}
                              >
                                <option value="">-- Spalte auswählen --</option>
                                {headers.map((header, index) => (
                                  <option key={index} value={header}>
                                    {header}
                                    {getExampleValue(header) && ` • "${getExampleValue(header).substring(0, 20)}..."`}
                                    {isAutoSelected(header, field) && ' ✓'}
                                  </option>
                                ))}
                              </select>
                              {(columnMapping as any)[field] && (
                                <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                  <CheckCircleIcon className="h-5 w-5 text-primary-500" />
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="mt-6 flex justify-between items-center">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {Object.values(columnMapping).filter(Boolean).length} von 6 Spalten zugeordnet
                        </div>
                        <button
                          onClick={handleCheckImport}
                          disabled={
                            !file ||
                            !selectedAccount ||
                            !columnMapping.buchungstag ||
                            !columnMapping.zahlungsbeteiligter ||
                            !columnMapping.buchungstext ||
                            !columnMapping.verwendungszweck ||
                            !columnMapping.betrag ||
                            !columnMapping.saldo ||
                            isChecking
                          }
                          className="px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed transition-all flex items-center gap-2 font-medium"
                        >
                          {isChecking ? (
                            <>
                              <div className="h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                              Prüfe Daten...
                            </>
                          ) : (
                            <>
                              <CheckIcon className="h-4 w-4" />
                              Daten prüfen & importieren
                            </>
                          )}
                        </button>
                      </div>
                    </section>
                  </>
                )}
              </div>
            </div>

            {/* Footer: Abbrechen LINKS */}
            <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex items-center justify-between">
                <button
                  onClick={handleClose}
                  className="px-5 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium"
                >
                  Abbrechen
                </button>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                  {file ? `${file.name} geladen` : 'Keine Datei ausgewählt'}
                </div>
              </div>
            </div>

            <ImportPreviewModal />
          </div>
        </div>
      )}

      <ConfirmationModal
        visible={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        variant={confirmModal.variant}
        buttons={confirmModal.variant === 'two' ? {
          cancel: {
            text: 'Abbrechen',
            onPress: () => setConfirmModal({ ...confirmModal, isOpen: false })
          },
          confirm: {
            text: 'OK',
            onPress: () => {
              confirmModal.onConfirm();
              setConfirmModal({ ...confirmModal, isOpen: false });
            }
          }
        } : undefined}
        button={confirmModal.variant === 'single' ? {
          text: 'OK',
          onPress: () => {
            confirmModal.onConfirm();
            setConfirmModal({ ...confirmModal, isOpen: false });
          }
        } : undefined}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </>
  );
};

export default CSVImporter;